import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config";

const CACHE_DURATION = 60 * 1000; // 1 minute cache for dashboard

export const useDonorDashboardData = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const lastFetchTime = useRef(0);
  const cachedData = useRef(null);
  const isFetching = useRef(false);

  const fetchDashboardStats = useCallback(async (forceRefresh = false) => {
    const now = Date.now();

    if (isFetching.current) {
      return;
    }

    // Use cached data if available and not expired
    if (
      !forceRefresh &&
      cachedData.current &&
      now - lastFetchTime.current < CACHE_DURATION
    ) {
      setStats(cachedData.current);
      setErrorMessage("");
      return;
    }

    isFetching.current = true;

    try {
      const response = await fetch(`${config.apiUrl}/donor/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        cachedData.current = data;
        lastFetchTime.current = Date.now();
        setStats(data);
        setErrorMessage("");
      } else {
        console.error("Failed to fetch dashboard stats:", data.message);
        setErrorMessage(data.message || "Failed to load dashboard data.");
      }
    } catch (error) {
      console.error("Network error fetching dashboard stats:", error);
      setErrorMessage("Unable to connect to the server.");
    } finally {
      isFetching.current = false;
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await fetchDashboardStats(false);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboardStats]);

  const refresh = useCallback(async () => {
    await fetchDashboardStats(true);
  }, [fetchDashboardStats]);

  return {
    loading,
    stats,
    errorMessage,
    refresh,
  };
};